/**
 * Tab Permission Utilities
 *
 * Utilities to get permission configuration for tabs within sections.
 */

import { Permission } from 'features/roles';
import {
  SECTION_PERMISSIONS,
  SectionConfig,
  TabActionConfig,
} from 'config/permissions.config';

/**
 * Gets the configuration for a section.
 *
 * @param section - The section name (e.g., 'projects', 'leads', 'inventory')
 * @returns The section configuration or undefined if not found
 *
 * @example
 * const config = getSectionConfig('projects');
 * // { basePermission: Permission.VIEW_PROJECTS, tabs: {...}, actions: {...} }
 */
export const getSectionConfig = (section: string): SectionConfig | undefined => {
  return SECTION_PERMISSIONS[section];
};

/**
 * Gets the base permission required for a section.
 *
 * @param section - The section name
 * @returns The base permission(s) or null if no base permission is required
 */
export const getSectionBasePermission = (
  section: string,
): Permission | Permission[] | null => {
  const config = getSectionConfig(section);
  return config?.basePermission ?? null;
};

/**
 * Checks if a section is admin-only.
 *
 * @param section - The section name
 * @returns True if the section requires admin access
 */
export const isSectionAdminOnly = (section: string): boolean => {
  const config = getSectionConfig(section);
  return config?.adminOnly === true;
};

/**
 * Gets the configuration for a specific tab in a section.
 *
 * @param section - The section name (e.g., 'projects')
 * @param tab - The tab name (e.g., 'budget', 'invoices')
 * @returns The tab configuration or undefined if not found
 *
 * @example
 * const tabConfig = getTabConfig('projects', 'budget');
 * // { view: [Permission.EDIT_PROJECT_BUDGETS], edit: [Permission.EDIT_PROJECT_BUDGETS] }
 */
export const getTabConfig = (
  section: string,
  tab: string,
): TabActionConfig | undefined => {
  const sectionConfig = getSectionConfig(section);
  return sectionConfig?.tabs[tab];
};

/**
 * Gets the permissions required to view a specific tab.
 *
 * @param section - The section name
 * @param tab - The tab name
 * @returns Array of permissions required to view the tab, or empty array
 *
 * @example
 * getTabViewPermissions('projects', 'budget');
 * // [Permission.EDIT_PROJECT_BUDGETS]
 */
export const getTabViewPermissions = (section: string, tab: string): Permission[] => {
  const tabConfig = getTabConfig(section, tab);
  return tabConfig?.view ?? [];
};

/**
 * Gets the permissions required to perform a specific action on a tab.
 *
 * @param section - The section name
 * @param tab - The tab name
 * @param action - The action name (e.g., 'edit', 'create', 'delete')
 * @returns Array of permissions required for the action, or empty array
 *
 * @example
 * getTabActionPermissions('projects', 'invoices', 'create');
 * // [Permission.CREATE_INVOICES]
 */
export const getTabActionPermissions = (
  section: string,
  tab: string,
  action: string,
): Permission[] => {
  const tabConfig = getTabConfig(section, tab);
  return (tabConfig?.[action] as Permission[]) ?? [];
};

/**
 * Gets the permissions required for a section-level action.
 *
 * @param section - The section name
 * @param action - The action name (e.g., 'create', 'edit', 'delete')
 * @returns Array of permissions required for the action, or empty array
 *
 * @example
 * getSectionActionPermissions('projects', 'create');
 * // [Permission.CREATE_PROJECTS, Permission.EDIT_PROJECT_BUDGETS, Permission.EDIT_TEMPLATES]
 */
export const getSectionActionPermissions = (
  section: string,
  action: string,
): Permission[] => {
  const sectionConfig = getSectionConfig(section);
  return sectionConfig?.actions?.[action] ?? [];
};

/**
 * Gets all tab names for a section.
 *
 * @param section - The section name
 * @returns Array of tab names
 *
 * @example
 * getSectionTabNames('projects');
 * // ['overview', 'tasks', 'budget', 'invoices', 'expenses', ...]
 */
export const getSectionTabNames = (section: string): string[] => {
  const sectionConfig = getSectionConfig(section);
  return sectionConfig ? Object.keys(sectionConfig.tabs) : [];
};

/**
 * Gets all tabs with their configurations for a section.
 *
 * @param section - The section name
 * @returns Record of tab names to their configurations
 */
export const getSectionTabs = (
  section: string,
): Record<string, TabActionConfig> => {
  const sectionConfig = getSectionConfig(section);
  return sectionConfig?.tabs ?? {};
};

